import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, ViewStyle, TextStyle, StyleProp } from 'react-native';

import { useTheme } from '../context/ThemeContext';
import { borderRadius, spacing, fontSize, fontWeight, accessibility } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

/** Button - themed pressable with variant, size, loading, and icon support. */
interface ButtonProps {
  readonly title: string;
  readonly onPress: () => void;
  readonly variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  readonly size?: 'sm' | 'md' | 'lg';
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly style?: StyleProp<ViewStyle>;
  readonly textStyle?: StyleProp<TextStyle>;
  readonly icon?: React.ReactNode;
  readonly accessibilityLabel?: string;
  readonly accessibilityHint?: string;
}

export default function Button({
  title, onPress, variant = 'primary', size = 'md',
  loading, disabled, style, textStyle, icon,
  accessibilityLabel, accessibilityHint,
}: ButtonProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const variantBgKey = `${variant}Bg` as keyof typeof styles;
  const sizeKey = `${size}Size` as keyof typeof styles;
  const variantTextKey = `${variant}Text` as keyof typeof styles;
  const sizeLabelKey = `${size}Label` as keyof typeof styles;

  const buttonStyle: ViewStyle[] = [
    styles.base as ViewStyle,
    styles[variantBgKey] as ViewStyle,
    styles[sizeKey] as ViewStyle,
    disabled ? styles.disabled as ViewStyle : undefined,
    style,
  ].filter(Boolean) as ViewStyle[];

  const labelStyle: TextStyle[] = [
    styles.label as TextStyle,
    styles[variantTextKey] as TextStyle,
    styles[sizeLabelKey] as TextStyle,
    textStyle,
  ].filter(Boolean) as TextStyle[];

  return (
    <TouchableOpacity
      style={buttonStyle}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || (loading ? `${title}, loading` : title)}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled: disabled || loading, busy: loading }}
    >
      {loading ? (
        <ActivityIndicator color={variant === 'outline' || variant === 'ghost' ? colors.primary : colors.white} />
      ) : (
        <>
          {icon}
          <Text style={labelStyle}>{title}</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: borderRadius.md,
    gap: spacing.sm,
    minHeight: accessibility.minTouchTarget,
    minWidth: accessibility.minTouchTarget,
  },
  primaryBg: { backgroundColor: c.primary },
  secondaryBg: { backgroundColor: c.surfaceSecondary },
  outlineBg: { backgroundColor: 'transparent', borderWidth: 1.5, borderColor: c.primary },
  dangerBg: { backgroundColor: c.danger },
  ghostBg: { backgroundColor: 'transparent' },
  smSize: { paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
  mdSize: { paddingVertical: spacing.md - 2, paddingHorizontal: spacing.lg },
  lgSize: { paddingVertical: spacing.md, paddingHorizontal: spacing.xl },
  label: { fontWeight: fontWeight.semibold },
  primaryText: { color: c.white, fontSize: fontSize.md },
  secondaryText: { color: c.text, fontSize: fontSize.md },
  outlineText: { color: c.primary, fontSize: fontSize.md },
  dangerText: { color: c.white, fontSize: fontSize.md },
  ghostText: { color: c.primary, fontSize: fontSize.md },
  smLabel: { fontSize: fontSize.sm },
  mdLabel: { fontSize: fontSize.md },
  lgLabel: { fontSize: fontSize.lg },
  disabled: { opacity: 0.5 },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
